import { useCallback, useEffect, useState } from 'react'
import { APP_VERSION } from '../version'
import { checkForUpdates } from '../utils/appUpdate'
import {
  getInstallPrompt,
  isInstalled,
  promptInstall,
  subscribeInstallPrompt,
} from '../utils/installPrompt'
import styles from './DownloadPage.module.css'

type Platform = 'ios' | 'android' | 'desktop'

const APK_URL = '/downloads/akash-player.apk'

function detectPlatform(): Platform {
  const ua = navigator.userAgent
  // iPadOS 13+ reports itself as Macintosh — the touch-point check catches it.
  if (
    /iPad|iPhone|iPod/.test(ua) ||
    (navigator.platform === 'MacIntel' && navigator.maxTouchPoints > 1)
  ) {
    return 'ios'
  }
  if (/Android/.test(ua)) return 'android'
  // "Request desktop site" mode hides the OS from the user agent, so a
  // phone can land on the desktop card. Fall back to hardware signals —
  // a coarse pointer on a small screen is a phone, whatever the UA says.
  const coarsePointer = window.matchMedia('(pointer: coarse)').matches
  const smallScreen = Math.min(window.screen.width, window.screen.height) < 820
  if (coarsePointer && smallScreen) {
    return /Macintosh/.test(ua) ? 'ios' : 'android'
  }
  return 'desktop'
}

const STEPS: Record<Platform, { title: string; icon: string; hint: string; steps: string[] }> = {
  ios: {
    title: 'iPhone / iPad',
    icon: '🍎',
    hint: 'Safari থেকে হোম স্ক্রিনে যোগ করুন — অ্যাপ স্টোর লাগবে না।',
    steps: [
      'Safari ব্রাউজারে এই পেজটি খুলুন (Chrome-এ কাজ করবে না)।',
      'নিচের শেয়ার বাটনে ট্যাপ করুন (⬆️ আইকন)।',
      '"Add to Home Screen" সিলেক্ট করুন → "Add" চাপুন।',
      'হোম স্ক্রিনে Akash Player আইকন চলে আসবে — ফুল স্ক্রিনে খুলবে।',
    ],
  },
  android: {
    title: 'Android',
    icon: '🤖',
    hint: 'এক ট্যাপে ইনস্টল — অ্যাপ স্টোর লাগবে না।',
    steps: [
      'Chrome ব্রাউজারে এই পেজটি খুলুন।',
      '"Install app" বাটনে ট্যাপ করুন (নিচের প্রাইমারি বাটন)।',
      'কনফার্ম করলেই ডেস্কটপে আইকন চলে আসবে।',
      'APK চাইলে নিচের ডাউনলোড লিংক থেকে সরাসরি নেওয়া যাবে।',
    ],
  },
  desktop: {
    title: 'Windows / Mac / Linux',
    icon: '🖥️',
    hint: 'ব্রাউজার থেকেই ইনস্টল করা যায়।',
    steps: [
      'Chrome বা Edge-এ এই পেজটি খুলুন।',
      'অ্যাড্রেস বারের ডানপাশে "Install" আইকনে ক্লিক করুন।',
      'অথবা উপরের বাটন থেকে সরাসরি ইনস্টল করুন।',
      'ডেস্কটপে শর্টকাট তৈরি হবে, আলাদা উইন্ডোতে খুলবে।',
    ],
  },
}

const AUTO_UPDATE = [
  {
    icon: '🔄',
    title: 'অটো আপডেট',
    desc: 'সার্ভারে নতুন ভার্সন গেলেই অ্যাপ নিজে নিজে আপডেট হয়ে যায় — আলাদা করে ডাউনলোড করতে হয় না।',
  },
  {
    icon: '⚡',
    title: 'ব্যাকগ্রাউন্ডে রেডি',
    desc: 'নতুন ভার্সন চুপিসারে ডাউনলোড হয়ে যায়; পরের বার খুললেই নতুন ভার্সন চলবে।',
  },
  {
    icon: '🎬',
    title: 'প্লেব্যাক বাধা পায় না',
    desc: 'ভিডিও চলাকালীন আপডেট কখনো পেজ রিলোড করে না — শেষ হওয়ার পর ট্যাপ করলেই হয়ে যায়।',
  },
]

export function DownloadPage() {
  const [platform, setPlatform] = useState<Platform>('desktop')
  const [standalone, setStandalone] = useState(false)
  const [hasPrompt, setHasPrompt] = useState(Boolean(getInstallPrompt()))
  const [apkAvailable, setApkAvailable] = useState(false)
  const [checking, setChecking] = useState(false)
  const [checkMsg, setCheckMsg] = useState('')

  useEffect(() => {
    setPlatform(detectPlatform())
    setStandalone(isInstalled())
    // The prompt itself is captured at module scope (utils/installPrompt.ts);
    // here we only mirror its current state into React.
    return subscribeInstallPrompt(() => {
      setStandalone(isInstalled())
      setHasPrompt(Boolean(getInstallPrompt()))
    })
  }, [])

  // The APK button only appears once the file is actually hosted — a HEAD
  // request (and the content-type guard) keeps us from linking to a 404 or to
  // the SPA fallback HTML.
  useEffect(() => {
    fetch(APK_URL, { method: 'HEAD' })
      .then((res) => {
        const type = res.headers.get('content-type') || ''
        if (res.ok && !type.includes('text/html')) setApkAvailable(true)
      })
      .catch(() => undefined)
  }, [])

  const handleInstall = useCallback(async () => {
    const outcome = await promptInstall()
    if (outcome === 'accepted') setStandalone(true)
  }, [])

  const handleCheck = useCallback(async () => {
    setChecking(true)
    setCheckMsg('')
    await checkForUpdates()
    setChecking(false)
    setCheckMsg('✅ সর্বশেষ ভার্সন চেক হয়েছে — কোনো নতুন আপডেট থাকলে অটো ইনস্টল হয়ে যাবে।')
  }, [])

  const current = STEPS[platform]
  const canInstall = hasPrompt && !standalone
  const ordered: Platform[] = [platform, ...(['ios', 'android', 'desktop'] as Platform[]).filter((p) => p !== platform)]

  return (
    <div className={styles.page}>
      <div className={styles.bgGradient} aria-hidden="true" />
      <div className={styles.bgGrid} aria-hidden="true" />

      {/* Hero */}
      <section className={styles.hero}>
        <div className={styles.container}>
          <div className={styles.appIcon} aria-hidden="true">
            <img src="/icons/icon-192.png" alt="" width={96} height={96} />
          </div>

          <div className={styles.badge}>
            <span className={styles.badgeDot} />
            <span>
              v{APP_VERSION} • {standalone ? 'Already Installed' : 'Ready to Install'}
            </span>
          </div>

          <h1 className={styles.heroTitle}>
            Download <span className={styles.gradientText}>Akash Player</span>
          </h1>
          <p className={styles.heroSubtitle}>
            এক লিংক, এক ট্যাপ — অ্যাপ স্টোর ছাড়াই ফোনে ইনস্টল হয়ে যাবে। পুরো অ্যাপ অটো-আপডেটেবল।
          </p>

          <div className={styles.platformChip}>
            {current.icon} Detected: {current.title}
          </div>

          <div className={styles.primaryRow}>
            {standalone ? (
              <div className={styles.installedNote}>
                ✅ অ্যাপ ইনস্টল করা আছে — হোম স্ক্রিন থেকে খুলুন।
              </div>
            ) : canInstall ? (
              <button id="btn-install-app" className={styles.primaryBtn} onClick={handleInstall}>
                ⬇ Install App
              </button>
            ) : platform === 'ios' ? (
              <a id="btn-add-homescreen" href="#ios-steps" className={styles.primaryBtn}>
                ⬆ Add to Home Screen
              </a>
            ) : (
              <a id="btn-install-steps" href="#install-steps" className={styles.primaryBtn}>
                📲 Install App
              </a>
            )}

            {apkAvailable && (
              <a
                id="link-apk"
                className={styles.secondaryBtn}
                href={APK_URL}
                download="akash-player.apk"
              >
                📦 Android APK
              </a>
            )}
          </div>

          {!standalone && !canInstall && (
            <p className={styles.menuHint}>
              {platform === 'ios'
                ? '💡 Safari-এর Share ⬆️ বাটন → "Add to Home Screen" থেকেও ইনস্টল করা যায়।'
                : '💡 Chrome-এর ⋮ মেনু → "Install app" / "Add to Home screen" থেকেও ইনস্টল করা যায়।'}
            </p>
          )}

          <p className={styles.heroNote}>
            ফ্রি • কোনো অ্যাকাউন্ট লাগবে না • iOS ও Android দুটোতেই একই অ্যাপ
          </p>
        </div>
      </section>

      {/* Install steps */}
      <section className={styles.section} id="install-steps" aria-label="Install steps">
        <div className={styles.container}>
          <div className={styles.sectionHeader}>
            <h2 className={styles.sectionTitle}>
              <span className={styles.sectionIcon}>📲</span>
              Install Steps
            </h2>
          </div>

          <div className={styles.stepsGrid}>
            {ordered.map((key, index) => {
              const card = STEPS[key]
              const isActive = key === platform
              return (
                <div
                  key={key}
                  id={key === 'ios' ? 'ios-steps' : undefined}
                  className={`${styles.stepCard} ${isActive ? styles.stepCardActive : ''}`}
                >
                  <div className={styles.stepCardHead}>
                    <span className={styles.stepIcon}>{card.icon}</span>
                    <strong className={styles.stepTitle}>{card.title}</strong>
                    {isActive && <span className={styles.youAreHere}>আপনার ডিভাইস</span>}
                    {!isActive && index > 0 && <span className={styles.stepSpacer} />}
                  </div>
                  <p className={styles.stepHint}>{card.hint}</p>
                  <ol className={styles.stepList}>
                    {card.steps.map((step, i) => (
                      <li key={i}>
                        <span className={styles.stepNum}>{i + 1}</span>
                        <span>{step}</span>
                      </li>
                    ))}
                  </ol>
                </div>
              )
            })}
          </div>
        </div>
      </section>

      {/* Auto update */}
      <section className={styles.section} aria-label="Auto update">
        <div className={styles.container}>
          <div className={styles.sectionHeader}>
            <h2 className={styles.sectionTitle}>
              <span className={styles.sectionIcon}>🔄</span>
              Auto Update
            </h2>
            <button
              id="btn-check-update"
              className={styles.checkBtn}
              onClick={handleCheck}
              disabled={checking}
            >
              {checking ? 'Checking…' : 'আপডেট চেক করুন'}
            </button>
          </div>

          <p className={styles.sectionDesc}>
            অ্যাপটা একবার ইনস্টল করলেই হবে — পরবর্তী সব আপডেট ওয়েবসাইট থেকেই অটো চলে আসবে।
            ডাউনলোড লিংক আবার আবার খুঁজতে হবে না।
          </p>

          {checkMsg && (
            <div className={styles.checkMsg} role="status" aria-live="polite">
              {checkMsg}
            </div>
          )}

          <div className={styles.updateGrid}>
            {AUTO_UPDATE.map((item) => (
              <div key={item.title} className={styles.updateCard}>
                <span className={styles.updateIcon}>{item.icon}</span>
                <div>
                  <strong className={styles.updateTitle}>{item.title}</strong>
                  <p className={styles.updateDesc}>{item.desc}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>
    </div>
  )
}
